package chat.amp.tim

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.ui.graphics.Color
import de.connect2x.messenger.compose.view.theme.DefaultAccentColor
import de.connect2x.messenger.compose.view.theme.ThemeDarkColorScheme
import de.connect2x.messenger.compose.view.theme.ThemeLightColorScheme
import org.koin.dsl.module

fun ampThemeModule() = module {
    single<DefaultAccentColor> {
        object : DefaultAccentColor {
            override val value = Brand.palette.accent
        }
    }
    single<ThemeLightColorScheme> {
        object : ThemeLightColorScheme {
            override fun create(accentColor: Color): ColorScheme =
                Brand.palette.light.toLightColorScheme(accentColor)
        }
    }
    single<ThemeDarkColorScheme> {
        object : ThemeDarkColorScheme {
            override fun create(accentColor: Color): ColorScheme =
                Brand.palette.dark.toDarkColorScheme(accentColor)
        }
    }
}

internal fun BrandColors.toLightColorScheme(accent: Color): ColorScheme = lightColorScheme(
    primary = primary.resolve(accent),
    onPrimary = onPrimary.resolve(accent),
    primaryContainer = primaryContainer.resolve(accent),
    onPrimaryContainer = onPrimaryContainer.resolve(accent),
    secondary = secondary.resolve(accent),
    onSecondary = onSecondary.resolve(accent),
    secondaryContainer = secondaryContainer.resolve(accent),
    onSecondaryContainer = onSecondaryContainer.resolve(accent),
    tertiary = tertiary.resolve(accent),
    onTertiary = onTertiary.resolve(accent),
    tertiaryContainer = tertiaryContainer.resolve(accent),
    onTertiaryContainer = onTertiaryContainer.resolve(accent),
    error = error.resolve(accent),
    errorContainer = errorContainer.resolve(accent),
    onError = onError.resolve(accent),
    onErrorContainer = onErrorContainer.resolve(accent),
    background = background.resolve(accent),
    onBackground = onBackground.resolve(accent),
    surface = surface.resolve(accent),
    onSurface = onSurface.resolve(accent),
    surfaceVariant = surfaceVariant.resolve(accent),
    onSurfaceVariant = onSurfaceVariant.resolve(accent),
    outline = outline.resolve(accent),
    inverseOnSurface = inverseOnSurface.resolve(accent),
    inverseSurface = inverseSurface.resolve(accent),
    inversePrimary = inversePrimary.resolve(accent),
    surfaceTint = surfaceTint.resolve(accent),
    outlineVariant = outlineVariant.resolve(accent),
    scrim = scrim.resolve(accent),
).withContainers(this, accent)

internal fun BrandColors.toDarkColorScheme(accent: Color): ColorScheme = darkColorScheme(
    primary = primary.resolve(accent),
    onPrimary = onPrimary.resolve(accent),
    primaryContainer = primaryContainer.resolve(accent),
    onPrimaryContainer = onPrimaryContainer.resolve(accent),
    secondary = secondary.resolve(accent),
    onSecondary = onSecondary.resolve(accent),
    secondaryContainer = secondaryContainer.resolve(accent),
    onSecondaryContainer = onSecondaryContainer.resolve(accent),
    tertiary = tertiary.resolve(accent),
    onTertiary = onTertiary.resolve(accent),
    tertiaryContainer = tertiaryContainer.resolve(accent),
    onTertiaryContainer = onTertiaryContainer.resolve(accent),
    error = error.resolve(accent),
    errorContainer = errorContainer.resolve(accent),
    onError = onError.resolve(accent),
    onErrorContainer = onErrorContainer.resolve(accent),
    background = background.resolve(accent),
    onBackground = onBackground.resolve(accent),
    surface = surface.resolve(accent),
    onSurface = onSurface.resolve(accent),
    surfaceVariant = surfaceVariant.resolve(accent),
    onSurfaceVariant = onSurfaceVariant.resolve(accent),
    outline = outline.resolve(accent),
    inverseOnSurface = inverseOnSurface.resolve(accent),
    inverseSurface = inverseSurface.resolve(accent),
    inversePrimary = inversePrimary.resolve(accent),
    surfaceTint = surfaceTint.resolve(accent),
    outlineVariant = outlineVariant.resolve(accent),
    scrim = scrim.resolve(accent),
).withContainers(this, accent)

/**
 * Applies the surface container roles a brand defines. Material fills the ones left out with
 * its baseline palette, which is purple tinted - visible in the room list, which the
 * messenger paints with `surfaceContainerLow`.
 */
private fun ColorScheme.withContainers(colors: BrandColors, accent: Color): ColorScheme = copy(
    surfaceDim = colors.surfaceDim?.resolve(accent) ?: surfaceDim,
    surfaceBright = colors.surfaceBright?.resolve(accent) ?: surfaceBright,
    surfaceContainerLowest = colors.surfaceContainerLowest?.resolve(accent) ?: surfaceContainerLowest,
    surfaceContainerLow = colors.surfaceContainerLow?.resolve(accent) ?: surfaceContainerLow,
    surfaceContainer = colors.surfaceContainer?.resolve(accent) ?: surfaceContainer,
    surfaceContainerHigh = colors.surfaceContainerHigh?.resolve(accent) ?: surfaceContainerHigh,
    surfaceContainerHighest = colors.surfaceContainerHighest?.resolve(accent) ?: surfaceContainerHighest,
)
